import { marked } from 'marked';

marked.setOptions({
  gfm: true,
  breaks: false,
});

function buildHtmlDocument(title: string, bodyHtml: string): string {
  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  @page { margin: 2cm; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 11pt;
    color: #222;
    line-height: 1.5;
    max-width: 760px;
    margin: 0 auto;
    padding: 24px;
  }
  h1 { font-size: 20pt; color: #3D5A45; margin: 0 0 12pt; }
  h2 { font-size: 14pt; color: #3D5A45; margin: 18pt 0 8pt; border-bottom: 1px solid #DDD8CE; padding-bottom: 4pt; }
  h3 { font-size: 12pt; color: #3D5A45; margin: 14pt 0 6pt; }
  p { margin: 6pt 0; }
  ul, ol { margin: 6pt 0 6pt 22pt; }
  li { margin: 2pt 0; }
  table { border-collapse: collapse; margin: 8pt 0; width: 100%; }
  th, td { border: 1px solid #DDD8CE; padding: 6pt 8pt; text-align: left; vertical-align: top; }
  th { background: #F0EDE5; }
  code { background: #F0EDE5; padding: 1pt 4pt; border-radius: 2pt; font-family: Consolas, monospace; font-size: 10pt; }
  pre { background: #F0EDE5; padding: 8pt; border-radius: 4pt; overflow-x: auto; }
  blockquote { border-left: 3px solid #81B29A; padding-left: 10pt; margin: 8pt 0; color: #555; }
  .meta { color: #A9A193; font-size: 9pt; margin-bottom: 18pt; }
</style>
</head>
<body>
<h1>${escapeHtml(title)}</h1>
<p class="meta">Généré par l'assistant IA du Portail BIM — ${new Date().toLocaleString('fr-FR')}</p>
${bodyHtml}
</body>
</html>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function safeFilename(title: string, ext: string): string {
  const base = title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  const date = new Date().toISOString().slice(0, 10);
  return `${date}_${base || 'reponse-ia'}.${ext}`;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Convertit markdown → HTML, puis ouvre une fenêtre avec le print dialog du navigateur. */
export async function exportMarkdownAsPdf(title: string, markdown: string) {
  const bodyHtml = await Promise.resolve(marked.parse(markdown));
  const html = buildHtmlDocument(title, bodyHtml as string);
  const w = window.open('', '_blank');
  if (!w) {
    alert('La fenêtre de prévisualisation a été bloquée. Autorise les pop-ups pour exporter.');
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  // Laisse au navigateur le temps de rendre le contenu avant d'appeler print()
  const onReady = () => {
    try {
      w.focus();
      w.print();
    } catch (e) {
      console.warn('Print failed', e);
    }
  };
  if (w.document.readyState === 'complete') onReady();
  else w.addEventListener('load', onReady);
}

/** Génère un .doc ouvrable par Word (HTML + MIME application/msword). */
export async function exportMarkdownAsWord(title: string, markdown: string) {
  const bodyHtml = await Promise.resolve(marked.parse(markdown));
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<meta name="ProgId" content="Word.Document">
<title>${escapeHtml(title)}</title>
<style>
body { font-family: Arial, sans-serif; font-size: 11pt; color: #222; }
h1 { font-size: 20pt; color: #3D5A45; }
h2 { font-size: 14pt; color: #3D5A45; }
h3 { font-size: 12pt; color: #3D5A45; }
table { border-collapse: collapse; }
th, td { border: 1pt solid #DDD8CE; padding: 6pt 8pt; }
th { background: #F0EDE5; }
</style>
</head>
<body>
<h1>${escapeHtml(title)}</h1>
<p style="color:#777;font-size:9pt;">Généré par l'assistant IA du Portail BIM — ${new Date().toLocaleString('fr-FR')}</p>
${bodyHtml}
</body></html>`;
  const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
  triggerDownload(blob, safeFilename(title, 'doc'));
}
